import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Truck, Plus, X, Mail, Phone, Globe, Search } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import purchaseOrderService from '../../services/purchaseOrders';
import { errorMessage } from '../../utils/apiResponse';
import type { Supplier, SupplierInput } from '../../types/purchaseOrders';

/**
 * /accounts/suppliers: who the organisation buys from, and where its
 * purchase orders are emailed.
 *
 * The organisation's own list, kept by any Accounts seat. People authorised
 * for purchase orders pick from it on a new order and change nothing here.
 * It replaced Spray Admin → Suppliers, which edited platform organisations
 * shared by every customer (2026-10-08).
 *
 * `?edit=<id>` opens that supplier, which is where an order with no email to
 * send to links.
 */

type Form = Record<keyof Omit<SupplierInput, 'active'>, string>;

const FIELDS: (keyof Form)[] = [
  'name', 'contact_email', 'po_email', 'contact_phone', 'website', 'abn',
  'address_line1', 'address_line2', 'city', 'state', 'postcode', 'country', 'notes',
];

function emptyForm(): Form {
  const f = Object.fromEntries(FIELDS.map(k => [k, ''])) as unknown as Form;
  f.country = 'Australia';
  return f;
}

function formFrom(s: Supplier): Form {
  return Object.fromEntries(FIELDS.map(k => [k, (s[k as keyof Supplier] as string | null) ?? ''])) as unknown as Form;
}

const inputClass = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="block min-w-0">
      <span className="block text-xs font-medium text-neutral-700 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-neutral-500 mt-1 break-words">{hint}</span>}
    </label>
  );
}

function Suppliers() {
  const [params, setParams] = useSearchParams();
  const [suppliers, setSuppliers] = useState<Supplier[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  // null: closed; 'new': adding; a supplier: editing it.
  const [editing, setEditing] = useState<Supplier | 'new' | null>(null);
  const [form, setForm] = useState<Form>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await purchaseOrderService.listSuppliers({ includeInactive: true });
      if (res.success && res.data) {
        setSuppliers(res.data);
        setLoadError(null);
      } else {
        setLoadError(errorMessage(res, 'Could not load the suppliers.'));
      }
    } catch (err) {
      setLoadError(errorMessage(err, 'Could not load the suppliers.'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = useCallback((target: Supplier | 'new') => {
    setEditing(target);
    setForm(target === 'new' ? emptyForm() : formFrom(target));
    setSaveError(null);
  }, []);

  // ?edit=<id>, from an order whose supplier has no email.
  const editParam = params.get('edit');
  useEffect(() => {
    if (!editParam || !suppliers) return;
    const s = suppliers.find(x => String(x.id) === editParam);
    if (s) open(s);
    setParams(p => { p.delete('edit'); return p; }, { replace: true });
  }, [editParam, suppliers, open, setParams]);

  const close = () => setEditing(null);

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [k]: e.target.value }));

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) { setSaveError('A supplier needs a name.'); return; }
    setSaving(true);
    setSaveError(null);
    try {
      const res = editing === 'new' || editing === null
        ? await purchaseOrderService.createSupplier(form)
        : await purchaseOrderService.updateSupplier(editing.id, form);
      if (!res.success) { setSaveError(errorMessage(res, 'Could not save the supplier.')); return; }
      await load();
      close();
    } catch (err) {
      setSaveError(errorMessage(err, 'Could not save the supplier.'));
    } finally {
      setSaving(false);
    }
  }

  async function setActive(s: Supplier, active: boolean) {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await purchaseOrderService.updateSupplier(s.id, { active });
      if (!res.success) { setSaveError(errorMessage(res, 'Could not change the supplier.')); return; }
      await load();
      close();
    } catch (err) {
      setSaveError(errorMessage(err, 'Could not change the supplier.'));
    } finally {
      setSaving(false);
    }
  }

  const q = search.trim().toLowerCase();
  const shown = (suppliers ?? [])
    .filter(s => showRetired || s.active)
    .filter(s => !q || s.name.toLowerCase().includes(q));
  const retiredCount = (suppliers ?? []).filter(s => !s.active).length;

  return (
    <>
      <PageHeader
        title="Suppliers"
        subtitle="Who you buy from, and where purchase orders are emailed"
        icon={<Truck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <p className="text-sm text-neutral-600 break-words">
          People authorised for <Link to="/purchase-orders" className="text-primary-600 underline">purchase orders</Link> choose
          from this list. An order is emailed to the supplier&rsquo;s purchase-order email, or its email when that is blank.
        </p>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex items-center gap-2 border border-neutral-300 rounded-lg px-3 py-2 bg-white flex-1 min-w-0">
            <Search className="h-4 w-4 text-neutral-400 shrink-0" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search suppliers"
              aria-label="Search suppliers"
              className="flex-1 min-w-0 text-sm focus:outline-none bg-transparent"
            />
          </div>
          <Button size="sm" onClick={() => open('new')}>
            <Plus className="h-4 w-4 mr-1" /> Add supplier
          </Button>
        </div>
        {retiredCount > 0 && (
          <label className="flex items-center gap-2 text-sm text-neutral-600">
            <input type="checkbox" checked={showRetired} onChange={e => setShowRetired(e.target.checked)} />
            Show retired suppliers ({retiredCount})
          </label>
        )}

        {loadError && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 break-words">{loadError}</p>
        )}

        {suppliers === null && !loadError ? (
          <p className="text-sm text-neutral-500 py-4">Loading…</p>
        ) : suppliers && suppliers.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-neutral-300 rounded-xl bg-white">
            <Truck className="h-8 w-8 text-neutral-300 mx-auto mb-2" />
            <p className="text-sm text-neutral-500">No suppliers yet</p>
            <button onClick={() => open('new')} className="mt-2 text-sm text-primary-600 underline">Add your first supplier</button>
          </div>
        ) : (
          <ul className="space-y-2">
            {shown.map(s => {
              const sendsTo = s.po_email || s.contact_email;
              return (
                <li key={s.id} className="flex items-start gap-3 border border-neutral-200 rounded-xl p-4 bg-white">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-neutral-900 break-words">
                      {s.name}
                      {!s.active && <span className="ml-2 text-xs font-medium text-neutral-500">Retired</span>}
                    </p>
                    <p className={`text-xs mt-1 break-all ${sendsTo ? 'text-neutral-600' : 'text-amber-700'}`}>
                      <Mail className="inline h-3 w-3 mr-1" />
                      {sendsTo ? `Orders go to ${sendsTo}` : 'No email: orders cannot be sent from here'}
                    </p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-neutral-500 min-w-0">
                      {s.contact_phone && <span><Phone className="inline h-3 w-3 mr-1" />{s.contact_phone}</span>}
                      {s.website && <span className="break-all"><Globe className="inline h-3 w-3 mr-1" />{s.website}</span>}
                      {typeof s.order_count === 'number' && s.order_count > 0 && (
                        <span>{s.order_count} {s.order_count === 1 ? 'order' : 'orders'}</span>
                      )}
                    </div>
                    {(s.address_line1 || s.city || s.state) && (
                      <p className="text-xs text-neutral-400 mt-1 break-words">
                        {[s.address_line1, s.city, s.state, s.postcode].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                  <button onClick={() => open(s)} className="text-xs text-primary-600 hover:underline shrink-0">Edit</button>
                </li>
              );
            })}
            {suppliers && shown.length === 0 && (
              <li className="text-sm text-neutral-500 py-2">No supplier matches &ldquo;{search}&rdquo;.</li>
            )}
          </ul>
        )}
      </div>

      {editing !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div role="dialog" aria-modal="true" aria-label={editing === 'new' ? 'Add supplier' : 'Edit supplier'}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
              <h3 className="text-base font-semibold text-neutral-900">{editing === 'new' ? 'Add supplier' : 'Edit supplier'}</h3>
              <button onClick={close} aria-label="Close" className="text-neutral-400 hover:text-neutral-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={save} className="p-5 space-y-3 overflow-y-auto max-h-[75vh]">
              <Field label="Name *">
                <input type="text" value={form.name} onChange={set('name')} required className={inputClass} />
              </Field>
              <Field label="Purchase-order email" hint="Purchase orders are emailed here. Blank uses the email below.">
                <input type="email" value={form.po_email} onChange={set('po_email')} className={inputClass} />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Email">
                  <input type="email" value={form.contact_email} onChange={set('contact_email')} className={inputClass} />
                </Field>
                <Field label="Phone">
                  <input type="tel" value={form.contact_phone} onChange={set('contact_phone')} className={inputClass} />
                </Field>
                <Field label="Website">
                  <input type="text" value={form.website} onChange={set('website')} placeholder="https://" className={inputClass} />
                </Field>
                <Field label="ABN">
                  <input type="text" value={form.abn} onChange={set('abn')} className={inputClass} />
                </Field>
              </div>
              <Field label="Address">
                <input type="text" value={form.address_line1} onChange={set('address_line1')} placeholder="Street address" className={inputClass} />
              </Field>
              <input type="text" value={form.address_line2} onChange={set('address_line2')} aria-label="Address line 2" className={inputClass} />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="col-span-2">
                  <Field label="Town">
                    <input type="text" value={form.city} onChange={set('city')} className={inputClass} />
                  </Field>
                </div>
                <Field label="State">
                  <input type="text" value={form.state} onChange={set('state')} className={inputClass} />
                </Field>
                <Field label="Postcode">
                  <input type="text" value={form.postcode} onChange={set('postcode')} className={inputClass} />
                </Field>
              </div>
              <Field label="Notes">
                <textarea value={form.notes} onChange={set('notes')} rows={2} className={inputClass} />
              </Field>

              {saveError && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 break-words">{saveError}</p>
              )}

              <div className="flex flex-wrap gap-3 pt-2">
                {editing !== 'new' && (
                  <Button type="button" variant="outline" disabled={saving} onClick={() => setActive(editing, !editing.active)}>
                    {editing.active ? 'Retire' : 'Bring back'}
                  </Button>
                )}
                <div className="flex gap-3 ml-auto">
                  <Button type="button" variant="outline" onClick={close}>Cancel</Button>
                  <Button type="submit" variant="primary" disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>
              {editing !== 'new' && editing.active && (
                <p className="text-[11px] text-neutral-500 break-words">
                  A retired supplier is not offered on a new order. Orders already raised keep it.
                </p>
              )}
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Suppliers;
